<template>
  <v-row no-gutters class="fill-height flex-column" style="overflow-y: auto; overflow-x: hidden;">
    <v-col cols="12" class="pr-6" v-if="isFetched && mapPanelDataFetched">
      <card-title-row :straightTitle="$i18n.t('sidebar.locationLab.reports')" class="pt-5 pb-3">
        <template #buttons>
          <base-rounded-button
            v-if="isEnlargmentVisible"
            icon="mdi-plus"
            size="x-small"
            color="success"
            :tooltip="$i18n.t('button.createReport')"
            @click="toggleReportDialog"
          />
        </template>
      </card-title-row>
      <sidebar-row flex-wrap="flex-nowrap" slot-cols="11" class="pb-3">
        <location-lab-reports-list
          ref="location-lab-reports-list"
          :reports="reports"
          :calculating="calculating"
          :enlargment="isEnlargmentVisible"
          @toggleReportGenerate="toggleReportGenerate"
        />
      </sidebar-row>
      <template v-if="calculating">
        <v-row no-gutters class="pt-10">
          <main-loader />
        </v-row>
      </template>
      <template v-else>
        <sidebar-row flex-wrap="flex-nowrap" slot-cols="11" border-top v-if="currentReport">
          <v-col cols="12">
            <v-row no-gutters align="center" class="pb-2">
              <v-col cols="auto">
                <v-row no-gutters class="location-lab-report-name">{{ currentReport.name }}</v-row>
                <v-row
                  no-gutters
                  class="location-lab-report-features"
                  v-if="!currentReport.has_multiple_layers_statistics"
                  >{{
                    $i18n.t('dialog.numberOfObjectsFrom', {
                      number:
                        currentReport.calculatedStats[0].features_count ||
                        currentReport.calculatedStats[0].total_features_count,
                      from: currentReport.calculatedStats[0].total_features_count,
                    })
                  }}</v-row
                >
              </v-col>
              <v-spacer />
              <v-col cols="auto">
                <base-rounded-button
                  icon="mdi-microsoft-excel"
                  size="x-small"
                  color="success"
                  :tooltip="$i18n.t('button.exportReportToXlsx')"
                  @click="getXlsx"
                />
              </v-col>
            </v-row>
            <v-row
              no-gutters
              v-for="(stat, idx) of currentReport.calculatedStats"
              :key="idx"
              align="center"
              class="py-3"
            >
              <v-col cols="12">
                <v-row no-gutters align="center">
                  <v-col cols="auto" class="text-body-2 pr-1">
                    {{ `${stat.name}${stat.values ? '' : ':'}` }}
                  </v-col>
                  <v-col cols="auto" class="location-lab-report-name" v-if="typeof stat.value !== 'object'">
                    {{ stat.value ? `${stat.value} ${stat.unit || ''}` : '' }}
                  </v-col>
                </v-row>
                <v-row no-gutters v-if="typeof stat.value === 'object'">
                  <v-col cols="12">
                    <v-row no-gutters align="center" v-for="(key, idx) of Object.keys(stat.value)" :key="idx">
                      <v-col cols="auto" class="text-body-2 pr-1"> {{ key }}: </v-col>
                      <v-col cols="auto" class="location-lab-report-name">
                        {{ stat.value[key] }}
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
                <template v-if="stat.values">
                  <v-row no-gutters align="center" v-for="(value, idx) of stat.values" :key="idx">
                    <v-col cols="auto" class="text-body-2 pr-1">
                      {{ value.text === null ? 'NULL' : value.text }}:
                    </v-col>
                    <v-col cols="auto" class="location-lab-report-name">
                      {{ value.value === null ? 'null' : `${value.value} ${stat.unit || ''}` }}
                    </v-col>
                  </v-row>
                </template>
                <template v-if="stat.values && stat.chartData">
                  <div class="pb-4 border-bottom">
                    <chart-bar
                      class="px-2 pt-2 mt-3 border"
                      cols="12"
                      v-bind="stat.chartData"
                      :name="`location-lab-chart-${idx}`"
                      :ref="`location-lab-chart-${idx}`"
                    />
                  </div>
                </template>
              </v-col>
            </v-row>
          </v-col>
        </sidebar-row>
      </template>
    </v-col>
    <main-loader v-else />
  </v-row>
</template>
<script>
import { debounce } from 'lodash';
import { call, get } from 'vuex-pathify';
import CardTitleRow from '@/components/CardTitleRow';
import ChartBar from '@/components/ChartBar';
import LocationLabReportsList from '@/components/LocationLabReportsList';
import MainLoader from '@/components/MainLoader';
import SidebarRow from '@/components/SidebarRow';
export default {
  name: 'LocationLabReports',
  components: {
    CardTitleRow,
    ChartBar,
    LocationLabReportsList,
    MainLoader,
    SidebarRow,
  },
  computed: {
    mapPanelDataFetched: get('map/mapPanelDataFetched'),
    currentLayerId: get('layers/currentLayer@id'),
    currentUser: get('users/currentUser'),
    reports: get('locationLab/reports'),
    selectedFeatures: get('map/selectedFeatures'),
    isEnlargmentEnable: get('admin/settingsValues@location_lab_edit_enabled'),
    isEnlargmentVisible() {
      return this.isEnlargmentEnable || this.currentUser?.is_superadmin;
    },
  },
  data: () => ({
    calculating: false,
    currentReport: undefined,
    currentReportXlsxPayload: undefined,
    isDialogVisible: false,
    isDialogLoaded: false,
    isFetched: false,
    newReport: { name: '', statistics: [] },
  }),
  methods: {
    calculateLocationLabReport: call('locationLab/calculateLocationLabReport'),
    getReports: call('locationLab/getLocationLabReports'),
    getLocationLabReportXlsx: call('locationLab/getLocationLabReportXlsx'),
    async init() {
      this.isFetched = false;
      try {
        await this.getReports();
        this.isFetched = true;
      } catch {
        this.isFetched = true;
      }
    },
    async getXlsx() {
      await this.getLocationLabReportXlsx(this.currentReportXlsxPayload);
    },
    async toggleReportGenerate(report) {
      this.calculating = true;
      try {
        const data = {
          id: report.id,
          payload: {
            ids: [],
          },
        };
        if (!report.has_multiple_layers_statistics) {
          const layer = report.statistics[0].layer;
          if (this.currentLayerId && this.currentLayerId == layer) {
            const selectedFeatures = this.selectedFeatures[layer];
            data.payload.ids = selectedFeatures || [];
          }
        }
        const r = await this.calculateLocationLabReport(data);
        this.currentReportXlsxPayload = { statistics: JSON.parse(JSON.stringify(r)) };
        const stats = [];
        for (const e in r) {
          const currentStat = report.statistics.find(statistic => statistic.name === e);
          const stat = r[e];
          stat.name = e;
          if (stat.values) {
            stat.values = this.$_sortObjectsByAttribute(stat.values, 'value', false, true);
            if (currentStat.with_chart && currentStat.chart_type) {
              stat.chartData = {
                data: stat.values.map(value => value.value),
                labels: stat.values.map(value => value.text),
                horizontal: currentStat.chart_type === 'bar_horizontal',
              };
            }
          }
          stat.unit = currentStat.unit;
          stats.push(stat);
        }
        this.currentReport = JSON.parse(JSON.stringify(report));
        this.currentReport.calculatedStats = stats;
        this.calculating = false;
      } catch {
        this.calculating = false;
      }
    },
    toggleReportDialog() {
      this.$refs?.['location-lab-reports-list']?.toggleEdit(this.newReport);
      this.isDialogLoaded = true;
      this.isDialogVisible = true;
    },
  },
  created() {
    this.getXlsx = debounce(this.getXlsx, 500);
  },
  mounted() {
    this.init();
  },
};
</script>
<style lang="scss" scoped>
.border {
  @include border(all, 1px);
}
.border-bottom {
  @include border(bottom, 2px);
}
.location-lab-report-name {
  font-family: Roboto;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 16px;
  color: #202124;
}
.location-lab-report-features {
  font-family: Roboto;
  font-style: italic;
  font-weight: 300;
  font-size: 12px;
  line-height: 16px;
  color: rgba(0, 0, 0, 0.59);
}
</style>
